import {
  Component,
  input,
  output,
  inject,
  effect,
  signal,
  computed,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { ModalComponent } from '../modal/modal';
import { HuxleyComponent } from '../huxley/huxley';
import { ModalStatusComponent } from '../modal-status/modal-status';
import { ContextDocumentUploadModal } from '../context-document-upload-modal/context-document-upload-modal';
import { InfoTipComponent } from '../info-tip/info-tip';
import { CONTEXT_DOC_SUPPORTED_FORMATS_LABEL } from '../../constants/context-document.constants';
import { deriveSelectionState } from '../../shared/picker-handler';
import { StepIndicatorComponent, StepConfig } from '../step-indicator/step-indicator';
import { SkeletonComponent } from '../skeleton/skeleton';
import { TypeIconComponent } from '../type-icon/type-icon';
import { KindIconComponent } from '../kind-icon/kind-icon';
import { CategoryIconComponent } from '../category-icon/category-icon';
import { ButtonComponent } from '../button/button';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { SelectComponent, type SelectOption } from '../select/select';
import { EmptyStateComponent } from '../empty-state/empty-state';
import { StateStoreService } from '../../services/state-store/state-store';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { VendorApi } from '../../services/vendor-api/vendor-api';
import {
  getContextDocIconConfig,
  getContextDocFileTypeLabel,
  getDocKindFromSubtypeCode,
  getCategoryKindFromCode,
} from '../../utils/document.utils';
import {
  getContextDocExtractionDisplay,
  type ExtractionStatusDisplay,
} from '../../utils/status-indicator.utils';
import { formatDate as formatDateUtil } from '../../utils/date.utils';
import { getDisplayAuthor } from '../../utils/display.utils';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';
import { accessDenialMessage } from '../../utils/access-error.utils';
import {
  Organization,
  type DocumentType,
  type DocumentSubtype,
  DocumentContentItemTypeEnum,
  ValidationRequest,
  ValidationResponse,
  ValidationTypeEnum,
  DocumentStatusEnum,
  ContextDocument,
  DocumentRecord,
  type EntityLanguage,
  DEFAULT_ENTITY_LANGUAGE,
} from '../../models';
import { LanguagePickerComponent } from '../language-picker/language-picker';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { getEntityLanguageLabel } from '../../utils/language.utils';
// Constants
type ModalStep = 'document-type' | 'language' | 'validation' | 'policy-documents' | 'review';

interface PolicyDocumentSelection {
  cookiePolicy: string | null;
  privacyPolicy: string | null;
  termsAndConditions: string | null;
}

@Component({
  selector: 'app-org-selector-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    TranslocoModule,
    ModalComponent,
    HuxleyComponent,
    ModalStatusComponent,
    StepIndicatorComponent,
    SkeletonComponent,
    TypeIconComponent,
    KindIconComponent,
    CategoryIconComponent,
    ButtonComponent,
    StatusIndicatorComponent,
    ContextDocumentUploadModal,
    InfoTipComponent,
    SelectComponent,
    EmptyStateComponent,
    LanguagePickerComponent,
    LanguageBadgeComponent,
  ],
  templateUrl: './org-selector-modal.html',
  styleUrl: './org-selector-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgSelectorModal {
  private readonly stateService = inject(StateStoreService);
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly vendorApi = inject(VendorApi);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationVersion = signal(0);
  readonly contentLanguage = signal<EntityLanguage>(DEFAULT_ENTITY_LANGUAGE);

  // Inputs
  isVisible = input<boolean>(false);

  /**
   * The create's progress and outcome. The POST is the parent's — this modal
   * only emits `documentCreationSelected` — so all of it comes back in, the
   * same shape as `<app-share-assessment-modal>`, where the parent likewise
   * owns the call.
   */
  readonly isCreating = input<boolean>(false);

  /** Set once the document exists; the modal then stays on its success view. */
  readonly successMessage = input<string | null>(null);

  /** Where the created document opened, for the "didn't open?" link. */
  readonly successOpenUrl = input<string | null>(null);
  /** True when the created document already opened in its own tab. */
  readonly successTabOpened = input(false);

  /** Why the create failed, if it did; the wizard stays on Review so the user can retry. */
  readonly createError = input<string | null>(null);

  /** Whether {@link createError} is a refusal rather than a failure — picks
   *  the amber-lock banner over the red one. */
  readonly createErrorIsDenial = input<boolean>(false);

  // Outputs
  documentCreationSelected = output<{
    orgId: string;
    /** Subtype code — used as document_content_type for generation (unchanged). */
    documentType: string;
    typeId: string;
    subtypeId: string;
    customTypeName?: string;
    orgName: string;
    documentName?: string;
    contextDocumentIds?: string[];
    cookiePolicyDocumentId?: string;
    privacyPolicyDocumentId?: string;
    termsAndConditionsDocumentId?: string;
    vendorListId?: string;
    language?: EntityLanguage;
  }>();
  cancelled = output<void>();

  // Org context — managers operate on the TopNav-picked customer; non-
  // managers operate on their own selected org. Single resolution, used
  // throughout the wizard.
  readonly isManager = this.stateService.isManager;
  protected readonly resolvedOrg = computed<Organization | null>(() => {
    const id = this.isManager()
      ? this.stateService.managedCustomerOrgId()
      : (this.stateService.selectedOrgId() ?? null);
    if (!id) return null;
    return this.stateService.organizations().find(o => o.principal_id === id) ?? null;
  });

  /** A new `_blank` tab gets a fresh sessionStorage, so forward the manager's
   *  current customer pick via a query param the vendors hub re-applies. */
  protected readonly vendorListsHref = computed<string>(() => {
    const managed = this.isManager() ? this.stateService.managedCustomerOrgId() : null;
    return managed
      ? `/vendors/lists?managedCustomer=${encodeURIComponent(managed)}`
      : '/vendors/lists';
  });
  isLoading = this.stateService.isLoading;

  private _loadedDocumentTypes = signal<DocumentType[]>([]);
  protected readonly _docTypeSubStep = signal<'category' | 'subtype'>('category');
  protected readonly _selectedCategory = signal<DocumentType | null>(null);
  protected readonly _selectedSubtype = signal<DocumentSubtype | null>(null);
  protected readonly _customTypeName = signal('');
  private _documentName = signal<string>('');
  private _currentStep = signal<ModalStep>('document-type');
  private _validationLoading = signal<boolean>(false);
  private _validationResult = signal<ValidationResponse | null>(null);
  private _validationError = signal<string | null>(null);
  private readonly validationReady = computed(
    () => !!this._validationResult() && !this._validationLoading() && !this._validationError()
  );
  /** Keyed by [[DocumentSubtypeCode]] — legacy enum values + any
   *  Postgres-era subtypes (e.g. `INFORMATION_SECURITY_POLICY`,
   *  `CUSTOM_*`) flow through the same maps. */
  private _approvingDocuments = signal<Set<string>>(new Set());
  private _selectedContextDocuments = signal<Set<string>>(new Set());
  private readonly _contextDocSearch = signal('');
  private _showUploadModal = signal<boolean>(false);
  private _selectedPolicyDocuments = signal<PolicyDocumentSelection>({
    cookiePolicy: null,
    privacyPolicy: null,
    termsAndConditions: null,
  });
  private _cookiePolicyDocuments = signal<DocumentRecord[]>([]);
  private _privacyPolicyDocuments = signal<DocumentRecord[]>([]);
  private _termsAndConditionsDocuments = signal<DocumentRecord[]>([]);
  private _vendorLists = signal<import('../../models').VendorList[]>([]);
  private _selectedVendorListId = signal<string | null>(null);

  /** Subtype code of the currently-selected subtype (replaces the old enum signal). */
  private readonly _selectedSubtypeCode = computed(() => this._selectedSubtype()?.code ?? null);

  // Public readonly state
  public readonly selectedDocumentType = this._selectedSubtypeCode;
  public readonly documentName = this._documentName.asReadonly();
  public readonly currentStep = this._currentStep.asReadonly();

  /**
   * High-water mark for the step timeline. Advances only on forward
   * navigation; stays put on Back and timeline-click. Reset by
   * `resetModalState` and whenever category/subtype changes on the
   * doc-type step (a different subtype reshapes `stepConfigs()` —
   * DPA adds a `policy-documents` step — so any prior visited state
   * past `document-type` may reference an old flow shape).
   */
  private readonly _maxReachedStep = signal<ModalStep>('document-type');
  public readonly maxReachedStep = this._maxReachedStep.asReadonly();

  /** Forward-move helper — pairs step-set with high-water bookkeeping. */
  private setStep(next: ModalStep): void {
    this._currentStep.set(next);
    const list = this.stepConfigs();
    const nextIdx = list.findIndex(s => s.key === next);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (nextIdx > maxIdx) {
      this._maxReachedStep.set(next);
    }
  }

  /**
   * Timeline click — jump to a previously-visited step. Selection
   * state (category, subtype, custom name, context-doc picks, policy-
   * doc picks, vendor list) all persist across jumps, so revisiting
   * is lossless. The step-indicator already gates to visited-and-not-
   * current; verify again here for defence in depth.
   */
  protected onStepClicked(key: string): void {
    if (this.isCreating()) return;
    const list = this.stepConfigs();
    const clickedIdx = list.findIndex(s => s.key === key);
    const maxIdx = list.findIndex(s => s.key === this._maxReachedStep());
    if (clickedIdx < 0 || clickedIdx > maxIdx) return;
    const validationIdx = list.findIndex(s => s.key === 'validation');
    if (clickedIdx > validationIdx && !this.validationReady()) return;
    this._currentStep.set(key as ModalStep);
  }

  /** True only for document types that require selecting linked policy documents (currently DPA). */
  public readonly requiresPolicyDocumentsStep = computed(
    () => this._selectedSubtypeCode() === DocumentContentItemTypeEnum.DPA
  );

  public readonly stepConfigs = computed<StepConfig[]>(() => {
    this.translationVersion();
    // Org context isn't a wizard step — the modal's header-meta slot
    // already surfaces the managed customer via
    // `showManagedCustomerContext: true` in `modalConfig`.
    const steps: StepConfig[] = [
      {
        key: 'document-type',
        label: this.transloco.translate('modals.doc_creation.step_document_type'),
        detail: this.selectedDocumentTypeLabel() || this._selectedCategory()?.name,
      },
      // Language is a dedicated step immediately AFTER the type
      // classification — the doc-type step now focuses on picking a
      // category + subtype, and language stops competing with that
      // decision for visual real-estate.
      {
        key: 'language',
        label: this.transloco.translate('language_step.step_label'),
        detail: getEntityLanguageLabel(this.contentLanguage()),
      },
      {
        key: 'validation',
        label: this.transloco.translate('modals.doc_creation.step_context'),
        detail: this._selectedContextDocuments().size
          ? this.transloco.translate('step_indicator.selected_count', {
              count: this._selectedContextDocuments().size,
            })
          : undefined,
      },
    ];
    if (this.requiresPolicyDocumentsStep()) {
      steps.push({
        key: 'policy-documents',
        label: this.transloco.translate('modals.doc_creation.step_policies'),
      });
    }
    steps.push({
      key: 'review',
      label: this.transloco.translate('modals.doc_creation.step_review'),
    });
    return steps;
  });
  public readonly validationLoading = this._validationLoading.asReadonly();
  public readonly validationResult = this._validationResult.asReadonly();
  public readonly validationError = this._validationError.asReadonly();

  // Public readonly state for context documents
  public readonly selectedContextDocuments = this._selectedContextDocuments.asReadonly();
  public readonly contextDocSearch = this._contextDocSearch.asReadonly();

  public readonly selectedContextDocObjects = computed(() => {
    const ids = this._selectedContextDocuments();
    return this.getContextDocuments().filter(d => ids.has(d.context_document_id));
  });

  /**
   * Tri-state for the picker's "Select all" header checkbox over the currently
   * visible (search-filtered) docs. Shared helper from picker-handler so the
   * select-all UI here behaves identically to the controls/frameworks pickers
   * elsewhere in the app.
   */
  public readonly contextDocsSelectionState = computed(() =>
    deriveSelectionState(
      this.filteredContextDocsForModal().map(d => d.context_document_id),
      this._selectedContextDocuments()
    )
  );

  /** Toggles every visible doc on/off — selects all if any are missing, clears
   *  all otherwise. Preserves any selections outside the visible (filtered) set. */
  toggleAllContextDocs(): void {
    const visibleIds = this.filteredContextDocsForModal().map(d => d.context_document_id);
    this._selectedContextDocuments.update(ids => {
      const allSelected = visibleIds.every(id => ids.has(id));
      const next = new Set(ids);
      if (allSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  /**
   * Filtered + stably-ordered context-doc list (newest uploads first).
   * Order is deliberately NOT driven by selection — reordering rows when the
   * user ticks a checkbox is disorienting (the row they just touched jumps).
   */
  public readonly filteredContextDocsForModal = computed(() => {
    const q = this._contextDocSearch().toLowerCase().trim();
    const docs = q
      ? this.getContextDocuments().filter(
          d =>
            d.file_name.toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q)
        )
      : this.getContextDocuments();
    return [...docs].sort(
      (a, b) => new Date(b.uploaded_at ?? 0).getTime() - new Date(a.uploaded_at ?? 0).getTime()
    );
  });

  getContextDocIconConfig = getContextDocIconConfig;
  getContextDocFileTypeLabel = getContextDocFileTypeLabel;
  getDisplayAuthor = getDisplayAuthor;
  getDocKindFromSubtypeCode = getDocKindFromSubtypeCode;
  getCategoryKindFromCode = getCategoryKindFromCode;

  protected readonly contextDocsHelpText = computed(() => {
    this.translationVersion();
    return this.transloco.translate('document_grounding.source_hint', {
      formats: CONTEXT_DOC_SUPPORTED_FORMATS_LABEL,
    });
  });

  public readonly showUploadModal = this._showUploadModal.asReadonly();

  // Public readonly state for policy document selection
  public readonly selectedPolicyDocuments = this._selectedPolicyDocuments.asReadonly();
  public readonly cookiePolicyDocuments = this._cookiePolicyDocuments.asReadonly();
  public readonly privacyPolicyDocuments = this._privacyPolicyDocuments.asReadonly();
  public readonly termsAndConditionsDocuments = this._termsAndConditionsDocuments.asReadonly();
  public readonly vendorLists = this._vendorLists.asReadonly();
  public readonly selectedVendorListId = this._selectedVendorListId.asReadonly();

  /** Adapters for `<app-select variant="form">` — pick lists rendered
   *  in the Linked Policies + Annex B steps. Each slot is single-select
   *  with "no pick" represented as `null`; the placeholder shows
   *  "— None —" (policies) or "AI-generated (no list)" (vendor list). */
  public readonly cookiePolicyOptions = computed<readonly SelectOption[]>(() =>
    this._cookiePolicyDocuments().map(doc => ({
      value: doc.document_id,
      label: this.getDocumentDisplayName(doc),
    }))
  );
  public readonly privacyPolicyOptions = computed<readonly SelectOption[]>(() =>
    this._privacyPolicyDocuments().map(doc => ({
      value: doc.document_id,
      label: this.getDocumentDisplayName(doc),
    }))
  );
  public readonly termsAndConditionsOptions = computed<readonly SelectOption[]>(() =>
    this._termsAndConditionsDocuments().map(doc => ({
      value: doc.document_id,
      label: this.getDocumentDisplayName(doc),
    }))
  );
  public readonly vendorListOptions = computed<readonly SelectOption[]>(() =>
    this._vendorLists().map(list => ({ value: list.list_id, label: list.name }))
  );

  /** Label of the currently-selected document type, used by the Review step. */
  public readonly selectedDocumentTypeLabel = computed(() => {
    const sub = this._selectedSubtype();
    if (!sub) return '';
    if (sub.code.startsWith('CUSTOM_') && this._customTypeName().trim()) {
      return this._customTypeName().trim();
    }
    return sub.name;
  });

  /**
   * Resolved policy-document picks for the Review step: each slot returns
   * the document's display name, or null if the user left it unset. Done
   * once here rather than three times in the template.
   */
  public readonly reviewPolicyDocuments = computed(() => {
    const sel = this._selectedPolicyDocuments();
    const nameFor = (id: string | null, pool: DocumentRecord[]) =>
      id ? (pool.find(d => d.document_id === id)?.name ?? null) : null;
    return {
      cookiePolicy: nameFor(sel.cookiePolicy, this._cookiePolicyDocuments()),
      privacyPolicy: nameFor(sel.privacyPolicy, this._privacyPolicyDocuments()),
      termsAndConditions: nameFor(sel.termsAndConditions, this._termsAndConditionsDocuments()),
    };
  });

  /** Categories loaded from GET /document-types (AI Risk Assessment subtypes
   *  excluded at display time). The `custom` category is pinned last — it's a
   *  catch-all, so the named categories should lead. Stable sort otherwise. */
  public readonly documentTypes = computed(() =>
    [...this._loadedDocumentTypes()].sort(
      (a, b) => Number(a.code === 'custom') - Number(b.code === 'custom')
    )
  );

  /** True when the selected category is the `custom` catch-all. Its single
   *  generic subtype is auto-selected, so the subtype list is suppressed and
   *  the user goes straight to the custom-name field. */
  protected readonly isCustomCategory = computed(() => this._selectedCategory()?.code === 'custom');

  /**
   * Modal config \u2014 only per-step content differs (title + primary button).
   *
   * Size is pinned to 'large' for every step so the dialog footprint never
   * changes as the user navigates. The .step-body wrapper inside each step's
   * template enforces a stable min-height for the same reason \u2014 every other
   * create wizard in the app (assessment, task, framework, control, vendor)
   * follows this convention.
   */
  get modalConfig() {
    // Subscribe to translation changes so titles/buttons update on lang switch.
    this.translationVersion();
    const step = this._currentStep();
    const base = {
      size: 'large' as const,
      showCloseButton: true,
      showFooter: true,
      showLogo: true,
      showManagedCustomerContext: true,
    };
    const next = this.transloco.translate('modals.doc_creation.primary_next');
    const back = this.transloco.translate('modals.doc_creation.secondary_back');
    const cancel = this.transloco.translate('modals.doc_creation.secondary_cancel');
    const cont = this.transloco.translate('modals.doc_creation.primary_continue');

    if (step === 'validation') {
      return {
        ...base,
        title: this.transloco.translate('modals.doc_creation.title_context_documents'),
        primaryButtonText: cont,
        secondaryButtonText: back,
        primaryButtonDisabled: !this.validationReady(),
      };
    }

    if (step === 'policy-documents') {
      return {
        ...base,
        title: this.transloco.translate('modals.doc_creation.title_linked_policies'),
        primaryButtonText: cont,
        secondaryButtonText: back,
        primaryButtonDisabled: !this.validationReady(),
      };
    }

    if (step === 'review') {
      const docTypeLabel =
        this.selectedDocumentTypeLabel() ||
        this.transloco.translate('modals.doc_creation.document_fallback');
      // The created document opened in a new tab; the success view stays until the user is done with it.
      if (this.successMessage()) {
        return {
          ...base,
          title: docTypeLabel,
          primaryButtonText: this.transloco.translate('common.close'),
          primaryButtonDisabled: false,
        };
      }
      const isCreating = this.isCreating();
      return {
        ...base,
        title: isCreating
          ? this.transloco.translate('modals.doc_creation.title_creating', { type: docTypeLabel })
          : this.transloco.translate('modals.doc_creation.title_review'),
        showCloseButton: !isCreating,
        showFooter: !isCreating,
        primaryButtonText: this.transloco.translate('modals.doc_creation.primary_create', {
          type: docTypeLabel,
        }),
        secondaryButtonText: back,
        primaryButtonDisabled: !this.validationReady(),
      };
    }

    // Default: document-type step. Two sub-steps for the *picker* (category
    // → subtype), but Custom short-circuits to "category-only" — the subtype
    // is auto-selected on category click and the custom-name input renders
    // inline beneath the grid. So the footer must also be visible in the
    // category sub-step whenever Custom is the active selection.
    const inSubtypeStep = this._docTypeSubStep() === 'subtype';
    const isCustom = this.isCustomCategory();
    const showFooter = inSubtypeStep || isCustom;
    const selectDocTypeTitle = this.transloco.translate(
      'modals.doc_creation.title_select_doc_type'
    );
    const docTypeStepTitle: string =
      inSubtypeStep && this._selectedCategory()
        ? this._selectedCategory()!.name
        : selectDocTypeTitle;
    return {
      ...base,
      title: docTypeStepTitle,
      primaryButtonText: next,
      secondaryButtonText: showFooter ? back : cancel,
      showFooter,
      primaryButtonDisabled:
        !this._selectedSubtype() ||
        (isCustom && !this._customTypeName().trim()) ||
        !this._documentName().trim() ||
        this._validationLoading(),
    };
  }

  private wizardOrgId: string | null = null;
  private validationEpoch = 0;
  private vendorEpoch = 0;

  constructor() {
    effect(() => {
      const orgId = this.resolvedOrg()?.principal_id ?? null;
      untracked(() => {
        if (this.wizardOrgId !== orgId) {
          this.wizardOrgId = orgId;
          this.resetModalState();
        }
      });
    });
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    this.orgManagementApi
      .getDocumentTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this._loadedDocumentTypes.set(res.document_types),
        error: () => this._loadedDocumentTypes.set([]),
      });

    // Reset state whenever the modal closes so a re-open lands fresh.
    effect(() => {
      if (!this.isVisible()) {
        this.resetModalState();
      }
    });
  }

  onCategorySelect(category: DocumentType): void {
    // A new category can reshape `stepConfigs()` (DPA subtype adds the
    // `policy-documents` step). Roll the high-water mark back to the
    // doc-type step so any prior "visited" state past it is invalidated
    // and the user re-walks the (possibly reshaped) flow forward.
    if (this._selectedCategory()?.id !== category.id) {
      this._maxReachedStep.set('document-type');
    }
    this._selectedCategory.set(category);
    this._selectedSubtype.set(null);
    this._customTypeName.set('');
    // Custom is a single-subtype catch-all — picking the category IS the
    // choice. Keep the user on the category sub-step (the custom-name input
    // renders inline beneath the grid via `isCustomCategory()`) instead of
    // advancing to a phantom subtype sub-step that just shows the same name
    // field with no other content and no movement in the step indicator.
    if (category.code === 'custom' && category.subtypes.length > 0) {
      this.onSubtypeSelect(category.subtypes[0]);
      return;
    }
    this._docTypeSubStep.set('subtype');
  }

  onSubtypeSelect(subtype: DocumentSubtype): void {
    // Subtype drives `requiresPolicyDocumentsStep()` which reshapes the
    // step list. Same reasoning as `onCategorySelect` — reset max so a
    // stale visited state past `document-type` can't survive a change.
    if (this._selectedSubtype()?.id !== subtype.id) {
      this._maxReachedStep.set('document-type');
    }
    this._selectedSubtype.set(subtype);
    if (!subtype.code.startsWith('CUSTOM_')) {
      this._customTypeName.set('');
      this._vendorLists.set([]);
      this._selectedVendorListId.set(null);
    }
    if (subtype.code !== DocumentContentItemTypeEnum.DPA) {
      this._vendorLists.set([]);
      this._selectedVendorListId.set(null);
    }
  }

  onCustomTypeNameChange(name: string): void {
    this._customTypeName.set(name);
  }

  onDocumentNameChange(name: string): void {
    this._documentName.set(name);
  }

  onContinue(): void {
    if (this.successMessage()) {
      this.onDismiss();
      return;
    }
    const step = this._currentStep();
    if (['validation', 'policy-documents', 'review'].includes(step) && !this.validationReady())
      return;

    switch (step) {
      case 'document-type':
        // Forward to the language step. Validation happens on the
        // language step's Continue (see below) so a slow validate
        // round-trip doesn't gate the type→language jump.
        this.setStep('language');
        break;
      case 'language':
        // Async — on success, lands on 'validation' (the Context step).
        this.validateDocument();
        break;
      case 'validation':
        // DPA inserts a Policies step between Context and Review; non-DPA
        // jumps straight to Review.
        this.setStep(this.requiresPolicyDocumentsStep() ? 'policy-documents' : 'review');
        break;
      case 'policy-documents':
        this.setStep('review');
        break;
      case 'review':
        this.handleDocumentCreation();
        break;
    }
  }

  private handleDocumentCreation(): void {
    if (!this.validationReady()) return;
    const orgId = this.resolvedOrg()?.principal_id;
    const subtype = this._selectedSubtype();
    const category = this._selectedCategory();
    const orgName = this.resolvedOrg()?.name;
    const selectedContextIds = Array.from(this._selectedContextDocuments());
    const selectedPolicyDocs = this._selectedPolicyDocuments();

    if (orgId && orgId === this.wizardOrgId && subtype && category && orgName) {
      const customName = this._documentName().trim();
      const isCustom = subtype.code.startsWith('CUSTOM_');
      this.documentCreationSelected.emit({
        orgId,
        documentType: subtype.code,
        typeId: category.id,
        subtypeId: subtype.id,
        customTypeName: isCustom ? this._customTypeName().trim() || undefined : undefined,
        orgName,
        documentName: customName || undefined,
        contextDocumentIds: selectedContextIds.length > 0 ? selectedContextIds : undefined,
        cookiePolicyDocumentId: selectedPolicyDocs.cookiePolicy || undefined,
        privacyPolicyDocumentId: selectedPolicyDocs.privacyPolicy || undefined,
        termsAndConditionsDocumentId: selectedPolicyDocs.termsAndConditions || undefined,
        vendorListId:
          subtype.code === DocumentContentItemTypeEnum.DPA
            ? this._selectedVendorListId() || undefined
            : undefined,
        language: this.contentLanguage(),
      });
    }
  }

  private validateDocument(): void {
    const orgId = this.resolvedOrg()?.principal_id;
    const documentType = this._selectedSubtypeCode();

    if (!orgId || !documentType) {
      return;
    }

    const epoch = ++this.validationEpoch;
    const isFirstLoad = this._validationResult() === null;
    this._validationLoading.set(true);
    this._validationError.set(null);
    if (isFirstLoad) this.setStep('validation');

    const validationRequest: ValidationRequest = {
      document_type: documentType,
      validation_type: ValidationTypeEnum.DOCUMENT,
    };

    this.orgManagementApi
      .validate(orgId, validationRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          if (epoch !== this.validationEpoch || orgId !== this.resolvedOrg()?.principal_id) return;
          // First load = arriving from `language` (the step whose Continue
          // triggers the validate call). Refreshes (e.g. after upload)
          // come from validation/policy-documents and must preserve
          // selection state and the current step the user is on.
          const previousDocIds = new Set(
            (this._validationResult()?.context_documents ?? []).map(
              (d: ContextDocument) => d.context_document_id
            )
          );

          this._validationResult.set(response);
          this._validationLoading.set(false);
          if (isFirstLoad) {
            this.setStep('validation');
          }

          if (isFirstLoad) {
            // Default to nothing selected — the user opts in to whichever docs they
            // want referenced. Auto-selecting every library doc was noisy and made
            // the "Select all" header redundant; opt-in matches what every other
            // multi-select picker in the app does.
            this._selectedContextDocuments.set(new Set());
          } else if (response.context_documents && response.context_documents.length > 0) {
            // Refresh (e.g. after upload) — preserve the user's current selection
            // and auto-tick docs they just uploaded in this session (the row they
            // added is clearly something they intend to use).
            const newIds = response.context_documents
              .map((d: ContextDocument) => d.context_document_id)
              .filter((id: string) => !previousDocIds.has(id));
            if (newIds.length > 0) {
              this._selectedContextDocuments.update(current => {
                const next = new Set(current);
                for (const id of newIds) next.add(id);
                return next;
              });
            }
          }

          // Extract policy documents from validation response
          this.extractPolicyDocumentsFromValidation(response);
        },
        error: error => {
          if (epoch !== this.validationEpoch || orgId !== this.resolvedOrg()?.principal_id) return;
          this._validationError.set(
            accessDenialMessage(error, this.transloco) ??
              getUserFriendlyErrorMessage(
                error,
                this.transloco.translate('context_documents.load_failed'),
                this.transloco
              )
          );
          this._validationLoading.set(false);
          this.setStep('validation');
        },
      });
  }

  onRetryValidation(): void {
    if (!this._validationLoading()) this.validateDocument();
  }

  private extractPolicyDocumentsFromValidation(response: ValidationResponse): void {
    const documentType = this._selectedSubtypeCode();

    // Only extract policy documents for DPA
    if (documentType !== DocumentContentItemTypeEnum.DPA) {
      return;
    }

    // Combine approved and pending documents
    const approvedDocuments = response.approved_documents || [];
    const pendingDocuments = response.pending_approval_documents || [];
    const allDocuments = [...approvedDocuments, ...pendingDocuments];

    // Map to DocumentRecord by changing document_type to type
    const mappedDocuments: DocumentRecord[] = allDocuments.map(
      doc =>
        ({
          ...doc,
          type: doc.document_type,
        }) as DocumentRecord
    );

    // Filter documents by type
    const cookiePolicyDocs = mappedDocuments.filter(
      doc => doc.type === DocumentContentItemTypeEnum.COOKIE_POLICY
    );
    const privacyPolicyDocs = mappedDocuments.filter(
      doc => doc.type === DocumentContentItemTypeEnum.PRIVACY_POLICY
    );
    const termsAndConditionsDocs = mappedDocuments.filter(
      doc => doc.type === DocumentContentItemTypeEnum.TERMS_AND_CONDITIONS
    );

    this._cookiePolicyDocuments.set(cookiePolicyDocs);
    this._privacyPolicyDocuments.set(privacyPolicyDocs);
    this._termsAndConditionsDocuments.set(termsAndConditionsDocs);

    // Load vendor lists for the DPA Annex B dropdown
    const orgId = this.resolvedOrg()?.principal_id;
    if (orgId) {
      const epoch = ++this.vendorEpoch;
      this.vendorApi
        .listVendorLists(orgId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: res => {
            if (epoch !== this.vendorEpoch || orgId !== this.resolvedOrg()?.principal_id) return;
            this._vendorLists.set(res.vendor_lists.filter(l => !l.archived_at));
          },
          error: () => {
            if (epoch !== this.vendorEpoch || orgId !== this.resolvedOrg()?.principal_id) return;
            this._vendorLists.set([]);
          },
        });
    }
  }

  onPolicyDocumentSelect(
    policyType: 'cookiePolicy' | 'privacyPolicy' | 'termsAndConditions',
    documentId: string | null
  ): void {
    const selectedId = documentId === '' || documentId === null ? null : documentId;
    this._selectedPolicyDocuments.update(current => ({
      ...current,
      [policyType]: selectedId,
    }));
  }

  onVendorListSelect(listId: string | null): void {
    this._selectedVendorListId.set(listId ? listId : null);
  }

  onBack(): void {
    if (this.isCreating()) return;
    const currentStep = this._currentStep();
    if (currentStep === 'document-type' && this._docTypeSubStep() === 'subtype') {
      // Within the type step: go back to category selection.
      this._docTypeSubStep.set('category');
      this._selectedSubtype.set(null);
      this._customTypeName.set('');
      return;
    }
    if (currentStep === 'document-type' && this.isCustomCategory()) {
      // Custom inline view (category sub-step + Custom selected). Back here
      // deselects the category so the user lands on a clean category picker.
      this._selectedCategory.set(null);
      this._selectedSubtype.set(null);
      this._customTypeName.set('');
      return;
    }
    if (currentStep === 'review') {
      this._currentStep.set(this.requiresPolicyDocumentsStep() ? 'policy-documents' : 'validation');
    } else if (currentStep === 'policy-documents') {
      this._currentStep.set('validation');
    } else if (currentStep === 'validation') {
      // Back from Context lands on Language — the validate call runs on
      // Language's Continue, so returning here means "let me re-pick
      // language before re-validating". Drop the cached validation so
      // the next Continue reissues the request in the (possibly new)
      // language.
      this._currentStep.set('language');
      this._validationResult.set(null);
      this._validationError.set(null);
      this._validationLoading.set(false);
    } else if (currentStep === 'language') {
      this._currentStep.set('document-type');
    } else {
      this.onCancel();
    }
  }

  /** Dismiss via X button or backdrop — after discard confirmation if dirty. */
  onDismiss(): void {
    if (this.isCreating()) return;
    this.resetModalState();
    this.cancelled.emit();
  }

  onCancel(): void {
    if (this.isCreating()) return;
    this.resetModalState();
    this.cancelled.emit();
  }

  readonly hasUnsavedWork = computed(() => this._selectedCategory() !== null);

  /**
   * Reset all modal state to initial values
   */
  private resetModalState(): void {
    this.validationEpoch++;
    this.vendorEpoch++;
    this._docTypeSubStep.set('category');
    this._selectedCategory.set(null);
    this._selectedSubtype.set(null);
    this._customTypeName.set('');
    this._documentName.set('');
    this._currentStep.set('document-type');
    this._maxReachedStep.set('document-type');
    this._validationLoading.set(false);
    this._validationResult.set(null);
    this._validationError.set(null);
    this._approvingDocuments.set(new Set());
    this._selectedContextDocuments.set(new Set());
    this._contextDocSearch.set('');
    this._showUploadModal.set(false);
    this._selectedPolicyDocuments.set({
      cookiePolicy: null,
      privacyPolicy: null,
      termsAndConditions: null,
    });
    this._cookiePolicyDocuments.set([]);
    this._privacyPolicyDocuments.set([]);
    this._termsAndConditionsDocuments.set([]);
    this._vendorLists.set([]);
    this._selectedVendorListId.set(null);
    this.contentLanguage.set(
      this.resolvedOrg()?.default_content_language ?? DEFAULT_ENTITY_LANGUAGE
    );
  }

  isApproving(documentId: string): boolean {
    return this._approvingDocuments().has(documentId);
  }

  private refreshValidationIfNeeded(): void {
    const orgId = this.resolvedOrg()?.principal_id;
    const docType = this._selectedSubtypeCode();
    if (orgId && docType) {
      this.validateDocument();
    }
  }

  // Context Documents Methods
  getContextDocuments(): ContextDocument[] {
    const result = this._validationResult();
    return result?.context_documents || [];
  }

  /** Same pill as create-assessment / import-assistant Context pickers. */
  protected extractionDisplay(doc: ContextDocument): ExtractionStatusDisplay | null {
    return getContextDocExtractionDisplay(doc);
  }

  isContextDocumentSelected(contextDocumentId: string): boolean {
    return this._selectedContextDocuments().has(contextDocumentId);
  }

  onContextDocSearchValue(value: string): void {
    this._contextDocSearch.set(value);
  }

  onContextDocumentToggle(contextDocumentId: string): void {
    this._selectedContextDocuments.update(selected => {
      const newSet = new Set(selected);
      if (newSet.has(contextDocumentId)) {
        newSet.delete(contextDocumentId);
      } else {
        newSet.add(contextDocumentId);
      }
      return newSet;
    });
  }

  /** Open the shared upload modal. Selected org must exist (button hidden otherwise). */
  onOpenUploadModal(): void {
    if (!this.resolvedOrg()) return;
    this._showUploadModal.set(true);
  }

  /**
   * Shared modal emits the successful uploads on close. If anything was uploaded we
   * re-run validation so the picker list shows the new docs (and they auto-select
   * via the `newIds` path in `validateDocument`).
   */
  onUploadModalClosed(uploaded: ContextDocument[]): void {
    this._showUploadModal.set(false);
    if (uploaded.length > 0) {
      this.refreshValidationIfNeeded();
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  readonly formatDate = (dateString: string) => formatDateUtil(dateString) || dateString;

  getDocumentDisplayName(doc: DocumentRecord): string {
    const statusLabel = doc.status === DocumentStatusEnum.ACTIVE ? 'Active' : 'Draft';
    const version = doc.version ? ` v${doc.version}` : '';
    const date = this.formatDate(doc.last_updated);
    return `${doc.name}${version} • ${statusLabel} • Updated ${date}`;
  }
}
